import { useEffect, useMemo, useState } from 'react'
import { useForm, type Resolver } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Alert, Box, Button, Paper, Stack, Typography } from '@mui/material'
import { ConfirmDialog } from '@components/common/ConfirmDialog'
import type { ScoringOptionsDto, ScoringOptionsResponse } from '@api/types/scoring'
import { ScoringKnobField } from './ScoringKnobField'
import { buildScoringSchema, fieldForKey } from './scoringSchema'

interface ScoringOptionsFormProps {
  data: ScoringOptionsResponse
  onSubmit: (values: ScoringOptionsDto) => void
  onReset: () => void
  isSaving?: boolean
  isResetting?: boolean
  /** Server-side validation message, if the last save was rejected. */
  serverError?: string | null
}

export function ScoringOptionsForm({
  data,
  onSubmit,
  onReset,
  isSaving,
  isResetting,
  serverError,
}: ScoringOptionsFormProps) {
  const schema = useMemo(() => buildScoringSchema(data.knobs), [data.knobs])

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = useForm<ScoringOptionsDto>({
    // The schema is assembled at runtime from the server's knobs, so its static shape is unknown here.
    resolver: zodResolver(schema) as unknown as Resolver<ScoringOptionsDto>,
    defaultValues: data.current,
  })

  // Re-seed whenever the server payload changes (initial load, save, reset).
  useEffect(() => {
    reset(data.current)
  }, [data, reset])

  const values = watch()
  const busy = !!isSaving || !!isResetting
  const [confirmReset, setConfirmReset] = useState(false)

  return (
    <Paper sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <Typography variant="h6" gutterBottom>
          Scoring knobs
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          A completion scores as <strong>adventure weight × percentile × collusion factor × repeat decay</strong>.
          Percentile is measured against other players on the same adventure, which is what makes a hike and a
          puzzle comparable. The maximum weight the formula can produce is {data.maxAchievableWeight}.
        </Typography>

        {serverError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {serverError}
          </Alert>
        )}

        {data.knobs.map((knob) => {
          const field = fieldForKey(knob.key)
          return (
            <ScoringKnobField
              key={knob.key}
              knob={knob}
              field={field}
              control={control}
              errors={errors}
              value={Number(values[field])}
              disabled={busy}
              onResetToDefault={() =>
                setValue(field, knob.default, { shouldDirty: true, shouldValidate: true })
              }
            />
          )
        })}

        <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" sx={{ mt: 2 }}>
          <Button type="submit" variant="contained" disabled={busy || !isDirty}>
            {isSaving ? 'Saving…' : 'Save changes'}
          </Button>
          <Button variant="outlined" onClick={() => reset(data.current)} disabled={busy || !isDirty}>
            Discard changes
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            color="warning"
            onClick={() => setConfirmReset(true)}
            disabled={busy || data.overrides.length === 0}
          >
            {isResetting ? 'Resetting…' : 'Reset all to defaults'}
          </Button>
        </Stack>
      </Box>

      {/* Unlike the per-knob reset icons (which only change the form until Save), this one calls
          the server straight away, so it says so. */}
      <ConfirmDialog
        open={confirmReset}
        title="Reset every scoring knob?"
        message={`This deletes all ${data.overrides.length} stored override${data.overrides.length === 1 ? '' : 's'} right now, with no Save step, and any unsaved edits in this form are lost. The knobs go back to the shipped defaults. Leaderboards do not move until the next recompute.`}
        confirmLabel="Reset to defaults"
        destructive
        loading={isResetting}
        onConfirm={() => {
          setConfirmReset(false)
          onReset()
        }}
        onClose={() => setConfirmReset(false)}
      />
    </Paper>
  )
}
